import "./galleryPanel.css";
import { Suspense, useEffect, useState } from "react";
import { getAwacTemplate } from "../awac/templateRegistry";
import TemplateErrorBoundary from "../awac/TemplateErrorBoundary";
import { t } from "../awac/i18n";
import { localizeAgentText } from "../awac/agentTextL10n";

/* Chevron path shared by the header's home-level arrows. */
const CHEVRONS = {
    prev: "M15.5 19a1 1 0 0 1-.7-.3l-6-6a1 1 0 0 1 0-1.4l6-6a1 1 0 1 1 1.4 1.4L11.4 12l5.8 5.3a1 1 0 0 1-.7 1.7z",
    next: "M8.5 19a1 1 0 0 1-.7-1.7l5.8-5.3-5.8-5.3a1 1 0 1 1 1.4-1.4l6 6a1 1 0 0 1 0 1.4l-6 6a1 1 0 0 1-.7.3z"
};

/**
 * GalleryPanel
 * ---------------------------------------------------------------------------
 * The gallery WING. It renders inside the same widget container as the chat,
 * as a second column to the LEFT of the chat. The chat column itself is left
 * untouched — it keeps showing the agent's intro line and the round-trip
 * option buttons. This wing only mounts when there is an active gallery
 * envelope, and can be dismissed with its close button (which collapses the
 * widget back to a single chat column) or with the Escape key.
 *
 * Templates are code-split (React.lazy) and rendered inside a Suspense
 * boundary, wrapped in a TemplateErrorBoundary so a template crash can never
 * take the conversation down with it.
 *
 * @param {object} props
 * @param {object} props.envelope - The parsed AWAC envelope to render.
 * @param {string} props.templateName - Registered template name (e.g. FloorPlanShowcase).
 * @param {Function} props.onClose - () => void. Dismisses the panel.
 * @param {Function} [props.onAction] - (action) => void. Optional. Forwarded to the
 *        template so card-level affordances (e.g. "Book a visit") can round-trip
 *        back to the chat as a normal text message.
 */
export default function GalleryPanel({ envelope, templateName, onClose, onAction }) {
    // Escape closes the gallery wing (but leaves the chat itself open).
    useEffect(() => {
        if (!envelope) {
            return undefined;
        }
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose && onClose();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [envelope, onClose]);

    // HOME-LEVEL PAGER lives in this header (not over the card) so the only
    // arrows near the photo are the photo's own. The mounted UnitCarousel
    // broadcasts its position via "awac:unit-pager" ({n, count, name}); the
    // header controls steer it back through "awac:prev-unit" / "awac:next-unit"
    // / "awac:goto-unit" — the same window-event bus the quick-action chip uses.
    const [pager, setPager] = useState(null);
    useEffect(() => {
        const onPager = (e) => {
            const d = e && e.detail;
            setPager(d && d.count > 0 ? d : null);
        };
        window.addEventListener("awac:unit-pager", onPager);
        // Mount-order safety: on REOPEN the (already-loaded) lazy template can
        // mount and broadcast before this parent effect subscribes — ask the
        // carousel to re-announce its position.
        window.dispatchEvent(new CustomEvent("awac:unit-pager-request"));
        return () => window.removeEventListener("awac:unit-pager", onPager);
    }, []);

    if (!envelope) {
        return null;
    }

    const Template = getAwacTemplate(templateName);
    const persAttrs = (Array.isArray(envelope.curation) && envelope.curation[0] &&
        Array.isArray(envelope.curation[0].personalizations) &&
        envelope.curation[0].personalizations[0]) || {};
    // Default panel header per template so the wing reads correctly for each.
    // Titles come from the i18n tables (gallery.<TemplateName>) so the panel
    // header follows the site language.
    const KNOWN_TITLE_TEMPLATES = [
        "FloorPlanShowcase",
        "UnitCarousel",
        "NeighborhoodMap",
        "BookingFrame",
        "BookingSlots",
        "BookingConfirm"
    ];
    const defaultTitle = KNOWN_TITLE_TEMPLATES.indexOf(templateName) !== -1
        ? t(`gallery.${templateName}`)
        : t("gallery.details");
    const rawTitle = persAttrs.title ||
        (persAttrs.attributes && persAttrs.attributes.IntroductionText);
    const title = rawTitle ? localizeAgentText(rawTitle, false) : defaultTitle;

    // A new template render (different template or envelope) resets a previous
    // error state so the fresh content gets a clean mount.
    const boundaryResetKey = `${templateName}:${envelope.text || ""}`;

    return (
        <>
        {/* Phone-only scrim behind the sheet (display:none on desktop). */}
        <div className="awacGallerySheetScrim" onClick={onClose} aria-hidden="true" />
        <section
            className="awacGalleryColumn"
            data-template={templateName || ""}
            role="complementary"
            aria-label={title}>
            <div className="awacGalleryPanelHeader">
                <span className="awacGalleryPanelTitle">{title}</span>
                {templateName === "UnitCarousel" && pager && pager.count > 1 && (
                    <div className="awacHeaderPager" role="group" aria-label={t("units.pagerOf", { n: pager.n, count: pager.count })}>
                        <button
                            type="button"
                            className="awacHeaderPagerArrow"
                            aria-label={t("units.prevUnit")}
                            title={t("units.prevUnit")}
                            onClick={() => window.dispatchEvent(new CustomEvent("awac:prev-unit"))}>
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d={CHEVRONS.prev} /></svg>
                        </button>
                        <span className="awacHeaderPagerLabel" aria-hidden="true">
                            {t("units.pagerOf", { n: pager.n, count: pager.count })}
                        </span>
                        {pager.count <= 8 && (
                            <span className="awacHeaderPagerDots">
                                {Array.from({ length: pager.count }, (_, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        className={`awacHeaderPagerDot${i + 1 === pager.n ? " isActive" : ""}`}
                                        aria-label={t("units.goToUnit", { name: `${i + 1}` })}
                                        aria-current={i + 1 === pager.n ? "true" : undefined}
                                        onClick={() => window.dispatchEvent(new CustomEvent("awac:goto-unit", { detail: { index: i } }))}
                                    />
                                ))}
                            </span>
                        )}
                        <button
                            type="button"
                            className="awacHeaderPagerArrow"
                            aria-label={t("units.nextUnit")}
                            title={t("units.nextUnit")}
                            onClick={() => window.dispatchEvent(new CustomEvent("awac:next-unit"))}>
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d={CHEVRONS.next} /></svg>
                        </button>
                    </div>
                )}
                <button
                    type="button"
                    className="awacGalleryPanelClose"
                    aria-label={t("gallery.close")}
                    title={t("gallery.close")}
                    onClick={onClose}>
                    &times;
                </button>
            </div>
            <div className="awacGalleryPanelBody">
                {Template ? (
                    <TemplateErrorBoundary resetKey={boundaryResetKey}>
                        <Suspense fallback={<div className="awacGalleryPanelLoading" aria-hidden="true" />}>
                            <Template payload={envelope} onAction={onAction} />
                        </Suspense>
                    </TemplateErrorBoundary>
                ) : (
                    <div className="awacGalleryPanelUnknown">
                        <p>{t("gallery.unknownTitle")}</p>
                        <small>{t("gallery.unknownSub")}{templateName ? `: ${templateName}` : ""}.</small>
                    </div>
                )}
            </div>
            {/* Phone sheet footer (design handoff §5): pinned Book + Next.
                Rendered only for the carousel; buttons drive the mounted
                template over the same window-event bus the pager uses. */}
            {templateName === "UnitCarousel" && (
                <div className="awacGallerySheetFooter">
                    <button
                        type="button"
                        className="awacGallerySheetBook"
                        onClick={() => window.dispatchEvent(new CustomEvent("awac:book-current-unit"))}>
                        {t("units.book")}
                    </button>
                    {pager && pager.count > 1 && (
                        <button
                            type="button"
                            className="awacGallerySheetNext"
                            onClick={() => window.dispatchEvent(new CustomEvent("awac:next-unit"))}>
                            {t("units.nextHome")}
                        </button>
                    )}
                </div>
            )}
        </section>
        </>
    );
}
